/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { OrderSchema, PaymentSchema } from "@repo/validators";
import type { z } from "zod";
import { useCallback, useEffect } from "react";
import ReceiptPrinterEncoder from "@point-of-sale/receipt-printer-encoder";
import {
  ClockTypes,
  OrderAuditTypes,
  OrderLineTypes,
  OrderStatus,
  PaymentStatus,
  Permissions,
  Psp,
  ReceiptType,
} from "@repo/database";
import {
  capitalize,
  formatIntToCurrency,
  generateRandomString,
  groupBy,
  printLeftRight,
} from "@repo/utils";
import { SplitOrderSchema } from "@repo/validators";
import { useNavigate } from "@tanstack/react-router";
import { format, isAfter, isBefore, startOfDay, subDays } from "date-fns";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { useShallow } from "zustand/react/shallow";

import type { ReceiptReport } from "./use-register-manager";
import type { CartItem } from "~/providers/cart-provider";
import type { Employee } from "~/providers/employee-provider";
import type { OnlineOrder, Order, OrderLine } from "~/providers/order-provider";
import type { Printer } from "~/providers/printing-provider";
import type { Table } from "~/providers/table-provider";
import type { Terminal } from "~/providers/terminal-provider";
import { useConfiguration } from "~/hooks/use-configuration";
import { useCourse } from "~/hooks/use-course";
import { useLabel } from "~/hooks/use-label";
import { useOrder } from "~/hooks/use-order";
import { usePrinter } from "~/hooks/use-printer";
import { useTable } from "~/hooks/use-table";
import { useUser } from "~/hooks/use-user";
import { api } from "~/lib/api";
import { fetchIpAddress } from "~/lib/utils";
import { useUnsavedChanges } from "~/providers/unsaved-changes-provider";
import { useAlertStore } from "~/stores/use-alert-store";
import { useTempBookingStore } from "~/stores/use-booking-store";
import { useCartStore } from "~/stores/use-cart-store";
import { useOrderStore } from "~/stores/use-order-store";
import { usePreferencesStore } from "~/stores/use-preferences-store";
import { useTableStore } from "~/stores/use-table-store";
import { usePlatform } from "../hooks/use-platform";
import { usePermissionManager } from "./use-permission-manager";

interface TransactionConfig {
  amount: number;
  terminal: Terminal;
  paymentId: string;
}

interface TransactionResult {
  id?: string;
  cancelUrl?: string;
  statusUrl?: string;
  errorId?: string;
}

function formatLine(line: string, width: number, align: string) {
  if (align === "center") {
    const padding = Math.max(0, (width - line.length) / 2);
    return (
      " ".repeat(Math.floor(padding)) + line + " ".repeat(Math.ceil(padding))
    );
  } else if (align === "right") {
    return " ".repeat(Math.max(0, width - line.length)) + line;
  }
  return line.padEnd(width);
}

export const useOrderManager = () => {
  const utils = api.useUtils();

  const { user, trialEnded } = useUser();

  const { orders } = useOrder();

  const { handleOrderUpdate, handleOrderDelete } = useOrderUpdates();

  const { courses } = useCourse();

  const { printRoutes, printers, sendToPrinter, getPrinterLanguage } =
    usePrinter();

  const { tables } = useTable();

  const {
    receiptSettings,
    preparationTicketSettings,
    cashRegisterConfiguration,
  } = useConfiguration();

  const { hasPermission, logout } = usePermissionManager();

  const { platform } = usePlatform();

  const navigate = useNavigate();

  const signOutAfterAction = usePreferencesStore(
    (state) => state.signOutAfterAction,
  );

  const { setUnsavedChanges } = useUnsavedChanges();

  const { labels } = useLabel();

  const {
    currentOrder,
    setCurrentOnlineOrder,
    setOpenOrdersOpen,
    setCheckoutOpen,
    setTransferDialogOpen,
    setTransferItems,
    setReceiptDialogOpen,
    setSelectedReceipt,
    setTransferingItems,
    setSplitDialogOpen,
    setIsLoading,
  } = useOrderStore();

  const { currentTable } = useTableStore();

  const { getNewItems, reset, items, setNoTableSelectedDialogOpen } =
    useCartStore(
      useShallow((state) => ({
        items: state.items,
        getNewItems: state.getNewItems,
        reset: state.reset,
        setNoTableSelectedDialogOpen: state.setNoTableSelectedDialogOpen,
      })),
    );

  const { t } = useTranslation("common");

  const { t: userT } = useTranslation("common", { lng: user.language });

  const { showDialog } = useAlertStore(
    useShallow((state) => ({
      showDialog: state.showDialog,
    })),
  );

  // const { mutate: sendReceiptByEmail, isPending: sendingEmail } =
  //   api.order.sendReceiptByEmail.useMutation({
  //     onSuccess() {
  //       toast.success(t("Checkout.receipt_sent_success"));
  //     },
  //     onError() {
  //       toast.error(t("Checkout.receipt_sent_error"));
  //     },
  //   });

  const { mutate: updateOrderAsync } = api.order.updateOrder.useMutation({
    async onMutate(updatedOrder) {
      await utils.order.getActiveOrders.cancel();

      const prevAll = utils.order.getActiveOrders.getData();

      function updateOrderData(
        oldData: Order[],
        updatedOrder: z.infer<typeof OrderSchema>,
        tableId?: string,
      ) {
        const updatedTotal = updatedOrder.orderLines.reduce(
          (acc, orderLine) => acc + orderLine.value,
          0,
        );

        const previousOrder = oldData.find(
          (order) => order.id === updatedOrder.id,
        );

        if (!previousOrder) {
          // New order
          return [
            ...oldData,
            {
              id: updatedOrder.id,
              discount: 0,
              items: updatedOrder.orderLines.map((orderLine) => ({
                ...orderLine,
                value: orderLine.value,
                printRouteId: orderLine.printRouteId,
                vatRateName: orderLine.vatRateName ?? "unknown",
                notifyPrintRouteId: orderLine.notifyPrintRouteId,
                parentReference: orderLine.parentReference,
                courseId: orderLine.courseId,
                productId: orderLine.productId,
                employeeId: orderLine.employeeId,
              })),
              tableId,
              total: updatedTotal,
              createdAt: new Date(),
              updatedAt: updatedOrder.updatedAt,
              currentRound: 1,
              paymentStatus: PaymentStatus.pending,
              partySize: updatedOrder.partySize,
              ticketPrinted: updatedOrder.printTicket ?? false,
              labelIds: updatedOrder.labelIds,
              name: updatedOrder.name,
              bookingId: undefined,
              creditId: undefined,
              payments: updatedOrder.payments.map((payment) => ({
                ...payment,
                paymentMethodName: payment.paymentMethodName ?? "",
                paymentStatus: payment.paymentStatus ?? PaymentStatus.pending,
              })),
              amountPaid: 0,
            },
          ];
        }

        // Update existing order
        return [
          ...oldData.filter((order) => order.id !== previousOrder.id),
          {
            id: previousOrder.id,
            discount: 0,
            items: previousOrder.items.concat(
              updatedOrder.orderLines.map((orderLine) => ({
                ...orderLine,
                ordered: true,
                printed: true,
                value: orderLine.value,
                vatRateName: orderLine.vatRateName ?? "unknown",
                printRouteId: orderLine.printRouteId,
                notifyPrintRouteId: orderLine.notifyPrintRouteId,
                turnoverGroupName: orderLine.turnoverGroupName ?? "unknown",
                turnoverGroupLedgerNumber: orderLine.turnoverGroupLedgerNumber,
                parentReference: orderLine.parentReference,
                productId: orderLine.productId,
                courseId: orderLine.courseId,
                employeeId: orderLine.employeeId,
              })),
            ),
            tableId: previousOrder.tableId,
            total: updatedTotal,
            createdAt: previousOrder.createdAt,
            updatedAt: updatedOrder.updatedAt,
            currentRound: previousOrder.currentRound,
            paymentStatus: PaymentStatus.pending,
            ticketPrinted: updatedOrder.printTicket ?? false,
            partySize: updatedOrder.partySize,
            labelIds: updatedOrder.labelIds,
            name: updatedOrder.name,
            bookingId: undefined,
            creditId: undefined,
            payments: updatedOrder.payments
              .map((payment) => ({
                ...payment,
                paymentMethodName: payment.paymentMethodName ?? "",
                paymentStatus: payment.paymentStatus ?? PaymentStatus.pending,
              }))
              .concat(previousOrder.payments),
            amountPaid: previousOrder.amountPaid,
          },
        ];
      }

      // Use the helper function in both query updates
      utils.order.getActiveOrders.setData(undefined, (old) => {
        if (!old) return prevAll;
        return updateOrderData(
          old,
          updatedOrder,
          updatedOrder.tableId ?? undefined,
        );
      });

      return { prevAll };
    },
  });

  const { mutate: updateOrder } = api.order.updateOrder.useMutation({
    async onMutate(updatedOrder) {
      await utils.order.getActiveOrders.cancel();

      const prevAll = utils.order.getActiveOrders.getData();

      function updateOrderData(
        oldData: Order[],
        updatedOrder: z.infer<typeof OrderSchema>,
        tableId?: string,
      ) {
        const updatedTotal = updatedOrder.orderLines.reduce(
          (acc, orderLine) => acc + orderLine.value,
          0,
        );

        const previousOrder = oldData.find(
          (order) => order.id === updatedOrder.id,
        );

        if (!previousOrder) {
          // New order
          return [
            ...oldData,
            {
              id: updatedOrder.id,
              discount: 0,
              items: updatedOrder.orderLines.map((orderLine) => ({
                ...orderLine,
                value: orderLine.value,
                printRouteId: orderLine.printRouteId,
                vatRateName: orderLine.vatRateName ?? "unknown",
                notifyPrintRouteId: orderLine.notifyPrintRouteId,
                parentReference: orderLine.parentReference,
                courseId: orderLine.courseId,
                productId: orderLine.productId,
                employeeId: orderLine.employeeId,
              })),
              tableId,
              total: updatedTotal,
              createdAt: new Date(),
              updatedAt: updatedOrder.updatedAt,
              currentRound: 1,
              paymentStatus: PaymentStatus.pending,
              partySize: updatedOrder.partySize,
              ticketPrinted: updatedOrder.printTicket ?? false,
              labelIds: updatedOrder.labelIds,
              name: updatedOrder.name,
              bookingId: undefined,
              creditId: undefined,
              payments: updatedOrder.payments.map((payment) => ({
                ...payment,
                paymentMethodName: payment.paymentMethodName ?? "",
                paymentStatus: payment.paymentStatus ?? PaymentStatus.pending,
              })),
              amountPaid: 0,
            },
          ];
        }

        // Update existing order
        return [
          ...oldData.filter((order) => order.id !== previousOrder.id),
          {
            id: previousOrder.id,
            discount: 0,
            items: previousOrder.items.concat(
              updatedOrder.orderLines.map((orderLine) => ({
                ...orderLine,
                ordered: true,
                printed: true,
                value: orderLine.value,
                vatRateName: orderLine.vatRateName ?? "unknown",
                printRouteId: orderLine.printRouteId,
                notifyPrintRouteId: orderLine.notifyPrintRouteId,
                turnoverGroupName: orderLine.turnoverGroupName ?? "unknown",
                turnoverGroupLedgerNumber: orderLine.turnoverGroupLedgerNumber,
                parentReference: orderLine.parentReference,
                productId: orderLine.productId,
                courseId: orderLine.courseId,
                employeeId: orderLine.employeeId,
              })),
            ),
            tableId: previousOrder.tableId,
            total: updatedTotal,
            createdAt: previousOrder.createdAt,
            updatedAt: updatedOrder.updatedAt,
            currentRound: previousOrder.currentRound,
            paymentStatus: PaymentStatus.pending,
            ticketPrinted: updatedOrder.printTicket ?? false,
            partySize: updatedOrder.partySize,
            labelIds: updatedOrder.labelIds,
            name: updatedOrder.name,
            bookingId: undefined,
            creditId: undefined,
            payments: updatedOrder.payments
              .map((payment) => ({
                ...payment,
                paymentMethodName: payment.paymentMethodName ?? "",
                paymentStatus: payment.paymentStatus ?? PaymentStatus.pending,
              }))
              .concat(previousOrder.payments),
            amountPaid: previousOrder.amountPaid,
          },
        ];
      }

      // Use the helper function in both query updates
      utils.order.getActiveOrders.setData(undefined, (old) => {
        if (!old) return prevAll;
        return updateOrderData(
          old,
          updatedOrder,
          updatedOrder.tableId ?? undefined,
        );
      });

      return { prevAll };
    },
    async onSuccess(_, { id }) {
      await handleOrderUpdate(id);
    },
    onError(err, _newPost) {
      // utils.order.getActiveOrders.setData(undefined, ctx?.prevAll);
      console.error(err);
      toast.error("Could not update order");

      // TODO: Handle this order offline.
    },
  });

  const { mutate: finalizeOrder } = api.order.finalizeOrder.useMutation({
    onSuccess() {
      setIsLoading(false);
    },
    onError() {
      setIsLoading(false);
    },
  });

  const { mutate: createAudit } = api.order.createAudit.useMutation();

  const { mutate: transferItems, isPending: transferingItems } =
    api.order.transferItems.useMutation({
      onMutate: async ({ items, orderId: newOrderId }) => {
        await utils.order.getActiveOrders.cancel();

        const oldOrderId = items[0]?.orderId;
        console.log(oldOrderId);
        const prevData = utils.order.getActiveOrders.getData();
        if (!oldOrderId) return;
        utils.order.getActiveOrders.setData(undefined, (old) => {
          if (!old) return prevData;
          return old.map((order) => {
            if (order.id === oldOrderId) {
              return {
                ...order,
                items: order.items.filter((item) => !items.includes(item)),
              };
            }
            if (order.id === newOrderId) {
              return {
                ...order,
                items: [...order.items, ...items],
              };
            }
            return order;
          });
        });
      },
      onSuccess(result) {
        toast.success(capitalize(t("Order.transfer_success")));

        if (result.oldOrderId) {
          if (result.shouldDeleteOldOrder === true) {
            handleOrderDelete(result.oldOrderId);
          } else {
            void handleOrderUpdate(result.oldOrderId);
          }
        }
        void handleOrderUpdate(result.newOrderId);
      },
      onError() {
        toast.error("Could not update order");
      },
    });

  const { mutate: deleteOrder } = api.order.delete.useMutation({
    // async onMutate(orderId) {
    //   await utils.order.getActiveOrders.cancel();

    //   const prevData = utils.order.getActiveOrders.getData();
    //   handleOrderDelete(orderId);

    //   return { prevData };
    // },
    onSuccess(_, variables) {
      setIsLoading(false);
      toast.success(capitalize(t("delete_item_success", { item: t("order") })));
      handleOrderDelete(variables);
    },
    onError() {
      setIsLoading(false);
    },
    // onError: () => {
    //   toast.error("Could not delete order");
    // },
    // async onSuccess() {
    //   await router.push("/floor-plan");
    // },
  });

  const { mutate: nextRound } = api.order.nextRound.useMutation({
    async onMutate(updatedOrder) {
      await utils.order.getActiveOrders.cancel();

      const prevData = utils.order.getActiveOrders.getData();

      utils.order.getActiveOrders.setData(undefined, (old) => {
        if (!old) return prevData;

        const previousOrder = old.find((order) => order.id === updatedOrder.id);

        if (!previousOrder) {
          return [...old];
        }

        return [
          ...old.filter((order) => order.id !== previousOrder.id),
          {
            ...previousOrder,
            current_round: updatedOrder.round,
            items: [
              ...previousOrder.items,
              {
                reference: generateRandomString(8),
                name: t("Ticket.next_round"),
                type: OrderLineTypes.comment,
                value: 0,
                quantity: 1,
                vatRate: 0,
                vatRateName: "unknown",
                productId: undefined,
                createdAt: new Date(),
                printRouteId: undefined,
                notifyPrintRouteId: undefined,
                courseId: undefined,
                parentReference: undefined,
                employeeId: undefined,
              },
            ],
          },
        ];
      });

      await navigate({ to: "/floor-plan" });

      return { prevData };
    },
    async onSuccess(_, variables) {
      await handleOrderUpdate(variables.id);
    },
  });

  const { mutate: startTransaction } = api.order.startTransaction.useMutation();
  const { mutate: startMollieTransaction } =
    api.order.startMollieTransaction.useMutation();

  const { mutate: splitOrder } = api.order.splitOrder.useMutation({
    async onSuccess(newOrderId, { orderId, tableId }) {
      toast.success(t("Order.split_success"));
      const newOrder = await handleOrderUpdate(newOrderId);
      await handleOrderUpdate(orderId);
      if (newOrder) {
        void navigate({
          to: "/cash-register",
          search: { orderId: newOrder.id, tableId: tableId },
        });
      }
    },
  });

  const { mutate: unFinalizeOrder } = api.order.unFinalizeOrder.useMutation({
    onSuccess() {
      toast.success(t("Checkout.order_unfinalized_success"));
    },
    onError() {
      toast.error("Could not unfinalize order");
    },
  });

  const { mutate: voidPayment } = api.order.voidPayment.useMutation({
    async onSuccess(result) {
      setIsLoading(false);

      toast.success(t("Checkout.payment_voided_success"));
      await handleOrderUpdate(result);
    },

    onError() {
      setIsLoading(false);
      toast.error("Could not void payment");
    },
  });

  const addPaymentHandler = async (
    data: z.infer<typeof PaymentSchema>,
    async?: boolean,
  ) => {
    const { employee, permission } = await hasPermission({
      requiredPermission: Permissions.checkout_order,
    });

    if (trialEnded) {
      await showDialog({
        title: t("trial_ended_title"),
        message: t("trial_ended_message"),
        buttons: [
          {
            label: t("ok"),
            value: "ok",
            variant: "default",
          },
        ],
      });
      return false;
    }

    return new Promise<boolean>((resolve) => {
      if (!permission) {
        return resolve(false);
      }

      const newItems = getNewItems();

      const itemsToPrint = items.filter((item) => item.printed === false);

      createPreparationTicketHandler({
        reference: currentOrder?.name,
        partySize: currentOrder?.partySize,
        employee: employee,
        table: currentTable,
        labelIds: currentOrder?.labelIds ?? [],
        items: itemsToPrint.map((item) => ({
          ...item,
          printRouteId: item.printRouteId,
          notifyPrintRouteId: item.notifyPrintRouteId,
          turnoverGroupName: item.turnoverGroupName,
          turnoverGroupLedgerNumber: item.turnoverGroupLedgerNumber,
          productId: item.productId,
          courseId: item.courseId,
          vatRateName: item.vatRateName ?? "unknown",
          parentReference: item.parentReference,
          employeeId: item.employeeId,
        })),
      }).catch((e) => {
        console.error(e);
        toast.error("Could not print ticket");
      });

      if (async) {
        updateOrderAsync(
          {
            updatedAt: new Date(),
            id: currentOrder?.id ?? uuidv4(),
            tableId: currentTable?.id,
            orderLines: newItems,
            employeeId: employee?.id,
            partySize: currentOrder?.partySize,
            name: currentOrder?.name,
            labelIds: currentOrder?.labelIds ?? [],
            payments: [data],
          },
          {
            async onSuccess(_, { id }) {
              await handleOrderUpdate(id);
              reset();

              setUnsavedChanges({
                isDirty: false,
              });

              return resolve(true);
            },
            onError(_err, _newPost) {
              toast.error("Could not update order");
              resolve(false);
            },
          },
        );
      } else {
        updateOrder({
          updatedAt: new Date(),
          id: currentOrder?.id ?? uuidv4(),
          tableId: currentTable?.id,
          orderLines: newItems,
          employeeId: employee?.id,
          partySize: currentOrder?.partySize,
          name: currentOrder?.name,
          labelIds: currentOrder?.labelIds ?? [],
          payments: [data],
        });

        reset();

        setUnsavedChanges({
          isDirty: false,
        });

        return resolve(true);
      }
    });
  };

  const createTerminalPaymentHandler = async ({
    amount,
    terminal,
    paymentId,
  }: TransactionConfig): Promise<TransactionResult> => {
    if (terminal.provider === Psp.pay) {
      const serviceId = cashRegisterConfiguration?.paySalesLocationId;

      if (!serviceId) {
        await showDialog({
          title: "Payment error",
          message: "Could not find Sales Location.",
          buttons: [
            {
              label: t("close"),
              value: "ok",
              variant: "destructive",
            },
          ],
        });
        return { errorId: "MISSING_SALES_LOCATION" };
      }

      const ipAddress = await fetchIpAddress();

      return new Promise((resolve) => {
        startTransaction(
          {
            amount,
            paymentId,
            terminalId: terminal.terminalId,
            reference: paymentId,
            ipAddress,
            serviceId,
          },
          {
            onSuccess: (result) => resolve(result),
            onError: () => resolve({ errorId: "PAY_TRANSACTION_FAILED" }),
          },
        );
      });
    }

    if (terminal.provider === Psp.mollie) {
      return new Promise((resolve) => {
        startMollieTransaction(
          {
            amount,
            paymentId,
            terminalId: terminal.terminalId,
            reference: paymentId,
          },
          {
            onSuccess: (result) => resolve(result),
            onError: () => resolve({ errorId: "MOLLIE_TRANSACTION_FAILED" }),
          },
        );
      });
    }

    return { errorId: "UNSUPPORTED_PROVIDER" };
  };

  const voidPaymentHandler = async (paymentId: string) => {
    if (trialEnded) {
      await showDialog({
        title: t("trial_ended_title"),
        message: t("trial_ended_message"),
        buttons: [
          {
            label: t("ok"),
            value: "ok",
            variant: "default",
          },
        ],
      });
      return false;
    }

    const { permission } = await hasPermission({
      requiredPermission: Permissions.update_order,
    });

    return new Promise<boolean>((resolve) => {
      if (!permission) {
        return resolve(false);
      }
      setIsLoading(true);

      voidPayment(paymentId, {
        onSuccess() {
          resolve(true);
        },
        onError() {
          resolve(false);
        },
      });
    });
  };

  const unfinalizeOrderHandler = async (orderId: string) => {
    if (trialEnded) {
      await showDialog({
        title: t("trial_ended_title"),
        message: t("trial_ended_message"),
        buttons: [
          {
            label: t("ok"),
            value: "ok",
            variant: "default",
          },
        ],
      });
      return null;
    }

    const { employee, permission } = await hasPermission({
      requiredPermission: Permissions.credit_order,
    });

    return new Promise<{ id: string; tableId?: string } | null>((resolve) => {
      if (!permission) {
        return resolve(null);
      }

      unFinalizeOrder(
        { orderId, employeeId: employee?.id },
        {
          onSuccess(result) {
            resolve(result);
          },
          onError() {
            resolve(null);
          },
        },
      );
    });
  };

  const deleteOrderHandler = async (orderId: string) => {
    if (trialEnded) {
      await showDialog({
        title: t("trial_ended_title"),
        message: t("trial_ended_message"),
        buttons: [
          {
            label: t("ok"),
            value: "ok",
            variant: "default",
          },
        ],
      });
      return null;
    }
    const { permission } = await hasPermission({
      requiredPermission: Permissions.delete_order,
    });

    if (!permission) return null;

    const order = orders.find((order) => order.id === orderId);

    if (!order) return null;

    if (
      order.payments.filter(
        (payment) => payment.paymentStatus === PaymentStatus.paid,
      ).length > 0
    ) {
      await showDialog({
        title: t("Order.before_delete_payment_title"),
        message: t("Order.before_delete_payment_message"),
        variant: "destructive",
        buttons: [
          {
            label: t("ok"),
            value: "cancel",
            variant: "default",
          },
        ],
      });
      return null;
    }

    const result = await showDialog({
      title: t("before_delete_order_title"),
      message: t("before_delete_order_message"),
      buttons: [
        {
          label: t("cancel"),
          value: "cancel",
          variant: "default",
        },
        {
          label: t("delete"),
          value: "ok",
          variant: "destructive",
        },
      ],
    });

    if (result === "cancel") return null;

    if (signOutAfterAction) {
      if (platform !== "ANDROID" && platform !== "IOS" && platform !== "PWA") {
        logout();
      }
    }

    reset();

    setUnsavedChanges({
      isDirty: false,
    });

    await navigate({ to: "/floor-plan", ignoreBlocker: true });

    return await new Promise<boolean>((resolve) => {
      setIsLoading(true);
      deleteOrder(orderId, {
        onSuccess() {
          resolve(true);
        },
        onError() {
          resolve(false);
        },
      });
    });
  };

  const nextRoundHandler = async (order: Order) => {
    if (trialEnded) {
      await showDialog({
        title: t("trial_ended_title"),
        message: t("trial_ended_message"),
        buttons: [
          {
            label: t("ok"),
            value: "ok",
            variant: "default",
          },
        ],
      });
      return null;
    }
    const { permission } = await hasPermission({
      requiredPermission: Permissions.update_order,
    });

    if (!permission) return null;

    nextRound({
      id: order.id,
      round: 1,
    });

    const printerIds =
      printRoutes.find(
        (printRoute) =>
          printRoute.id === cashRegisterConfiguration.nextRoundPrintRouteId,
      )?.printerIds ?? [];

    const p = printers.filter((printer) => printerIds.includes(printer.id));

    p.forEach((printer) => {
      const width = printer.width;

      const encoder = new ReceiptPrinterEncoder({
        language: getPrinterLanguage(printer.language),
        columns: width,
      });

      const result = encoder
        .initialize()
        .codepage("windows1252")
        .align("center")
        .width(2)
        .height(2)
        .bold(true)
        .line("");

      if (preparationTicketSettings?.tablePosition === 0) {
        result.line(
          userT("Ticket.table", {
            table_reference: tables.find((table) => table.id === order.tableId)
              ?.reference,
          }),
        );
      }

      result.newline().text(userT("Ticket.next_round"));

      result.newline();
      result.newline();

      if (preparationTicketSettings?.tablePosition === 1) {
        result.newline();

        result.line(
          userT("Ticket.table", {
            table_reference: tables.find((table) => table.id === order.tableId)
              ?.reference,
          }),
        );
      }

      result.newline();
      result.newline();
      result.newline();
      result.newline();
      result.newline();

      result.cut("partial");
      const encoded = result.encode();
      void sendToPrinter(encoded, printer);
    });

    return true;
  };

  const createPreparationTicketHandler = async ({
    items,
    employee,
    table,
    reference,
    partySize,
    labelIds,
  }: {
    items: OrderLine[];
    employee?: Employee;
    table?: Table;
    reference?: string;
    partySize?: number;
    labelIds: string[];
  }) => {
    const printerItemsMap: Record<string, OrderLine[]> = {};

    for (const item of items) {
      // get the main print route for the item
      const printRoute = printRoutes.find((pr) => pr.id === item.printRouteId);

      if (!printRoute) {
        console.error("No print route found for item", item);
        continue;
      }

      const mainPrinters = printers.filter((printer) =>
        printer.printRoutes.includes(printRoute.id),
      );

      for (const printer of mainPrinters) {
        if (!printerItemsMap[printer.id]) {
          printerItemsMap[printer.id] = [];
        }
        printerItemsMap[printer.id]!.push(item);
      }

      if (item.notifyPrintRouteId) {
        // get the notify print route for the item
        const notifyPrintRoute = printRoutes.find(
          (pr) => pr.id === item.notifyPrintRouteId,
        );

        if (!notifyPrintRoute) {
          console.error("No notify print route found for item", item);
        } else {
          const notifyPrinters = printers.filter((printer) =>
            printer.printRoutes.includes(notifyPrintRoute.id),
          );
          for (const notifyPrinter of notifyPrinters) {
            if (!printerItemsMap[notifyPrinter.id]) {
              printerItemsMap[notifyPrinter.id] = [];
            }
            printerItemsMap[notifyPrinter.id]!.push(item);
          }
        }
      }
    }

    // remove duplicates
    for (const key in printerItemsMap) {
      const seenIds = new Set<string>();
      printerItemsMap[key] = printerItemsMap[key]!.filter((orderLine) => {
        const isDuplicate = seenIds.has(orderLine.reference);
        seenIds.add(orderLine.reference);
        return !isDuplicate;
      });
    }

    const printPromises = Object.entries(printerItemsMap).map(
      async ([printerId, printerItems]) => {
        const printer = printers.find((p) => p.id === printerId);

        if (!printer) return;

        const linkedLabels = labels.filter((label) =>
          labelIds.includes(label.id),
        );

        const width = printer.width;

        const encoder = new ReceiptPrinterEncoder({
          language: getPrinterLanguage(printer.language),
          columns: width,
        });

        const result = encoder
          .initialize()
          .codepage("windows1252")
          .newline()
          .newline()
          .newline();

        const getTableInfo = () => {
          result.newline().newline().align("center");
          if (table?.reference) {
            result
              .height(2)
              .width(2)
              .bold(true)
              .line(
                userT("Ticket.table", { table_reference: table.reference }),
              );
          }

          // Add order reference if available
          if (reference) {
            result.height(1).width(2).line(reference);
          }

          result.bold(false);

          result.height(1).width(1).rule();

          if (linkedLabels.length > 0) {
            result.height(1).width(2);
            result
              .bold(true)
              .line(linkedLabels.map((label) => label.text).join(" - "));
            result.bold(false);
          }

          result.height(1).width(1).rule();

          // Add party size if available
          if (partySize) {
            result.bold(true).table(
              [
                { width: printer.width * 0.5, align: "left" },
                { width: printer.width * 0.5, align: "right" },
              ],
              [[t("Ticket.party_size", { size: partySize }), ""]],
            );
          }

          // Add employee name and current date/time
          result.bold(false).table(
            [
              { width: printer.width * 0.5, align: "left" },
              { width: printer.width * 0.5, align: "right" },
            ],
            [
              [
                employee ? employee.firstName : "",
                format(
                  new Date(),
                  user.clock === ClockTypes.military
                    ? "dd-MM-yyyy HH:mm"
                    : "MM/dd/yyyy hh:aa a",
                ),
              ],
            ],
          );
        };

        // Add table reference if available
        if (preparationTicketSettings?.tablePosition === 0) {
          getTableInfo();
          result.rule();
        }

        // Separate products and comments
        const products = printerItems.filter(
          (item) => item.type === OrderLineTypes.product,
        );

        const hasOnlyNotifyItems = products.every((product) => {
          const printRoute = printRoutes.find(
            (pr) => pr.id === product.printRouteId,
          );
          return !printRoute?.printerIds.includes(printer.id);
        });

        // Return early if all items are notify items
        if (hasOnlyNotifyItems) {
          return;
        }

        const comments = items.filter(
          (item) =>
            item.type === OrderLineTypes.comment && !item.parentReference,
        );

        // Print products without course
        const productsWithoutCourse = products.filter((item) => !item.courseId);

        if (productsWithoutCourse.length > 0) {
          printItems(productsWithoutCourse, result, printer);
          if (products.length > 1) {
            result.height(1).width(1);
            result.rule({ style: "single", width: width });
          }
        }

        // Print products with course
        courses.sort((a, b) => a.position - b.position);

        for (const course of courses) {
          const productsOnCourse = products.filter(
            (product) => product.courseId === course.id,
          );

          if (productsOnCourse.length === 0) continue;

          result
            .newline()
            .width(2)
            .height(2)
            .bold(true)
            .align("center")
            .line(course.name);

          printItems(productsOnCourse, result, printer);

          result.rule({ style: "single", width: width });
        }

        // Print order comments
        if (comments.length > 0) {
          result
            .newline()
            .newline()
            .align("center")
            .bold(true)
            .width(2)
            .line(userT("Ticket.comments"))
            .bold(false)
            .width(1);

          comments.forEach((comment) => {
            result.line(comment.name);
          });
        }

        // Add table reference if available
        if (
          table?.reference &&
          preparationTicketSettings?.tablePosition === 1
        ) {
          getTableInfo();
        }

        // Add necessary line breaks and cut the paper
        result
          .newline()
          .newline()
          .newline()
          .newline()
          .newline()
          .newline()
          .newline();

        result.cut("partial");

        const encoded = result.encode();

        await sendToPrinter(encoded, printer).catch((e) => console.error(e));
      },
    );

    await Promise.all(printPromises);
  };

  const printItems = (
    products: OrderLine[],
    result: ReceiptPrinterEncoder,
    printer: Printer,
  ) => {
    const groupedByName = groupBy(products, "name");

    result.width(2).height(1);

    Object.entries(groupedByName).forEach(([name, itemsByName]) => {
      const baseName = name.split("-")[0] ?? "";
      const printRouteId = itemsByName.at(0)?.printRouteId;

      if (!printRouteId) return;

      const isNotifyItem = !printRoutes
        .find((pr) => pr.id === printRouteId)
        ?.printerIds.includes(printer.id);

      let regularQuantity = itemsByName.reduce(
        (acc, item) => acc + item.quantity,
        0,
      );

      result.invert(regularQuantity < 0);

      itemsByName.forEach((item) => {
        const children = items.filter(
          (i) => i.parentReference === item.reference,
        );

        if (children.length > 0) {
          if (!isNotifyItem) {
            result.width(2).height(1);
          } else {
            result.width(1).height(1);
          }
          regularQuantity -= item.quantity;
          result
            .newline()
            .align("left")
            .line(`${item.quantity} ${baseName.toUpperCase()}`);

          children.forEach((child) => {
            if (child.type === OrderLineTypes.comment) {
              result
                .bold(false)
                .line(`   "${child.name.toUpperCase()}"`)
                .bold(true);
            } else if (child.type === OrderLineTypes.product) {
              result.line(`   +${child.name.toUpperCase()}`);
            }
          });
        }
      });

      if (regularQuantity === 0) return;

      if (!isNotifyItem) {
        result.width(2).height(1);
      } else {
        result.width(1).height(1);
      }

      result
        .newline()
        .align("left")
        .line(`${regularQuantity} ${baseName.toUpperCase()}`);
    });
    result.height(1).width(1);
  };

  const openOnlineOrderDialog = (order: OnlineOrder) => {
    setCurrentOnlineOrder(order);
  };

  const createTicket = async ({
    ticketItems,
    receiptNumber,
    tableReference,
    employeeName,
    receiptDate,
    payments,
  }: {
    ticketItems?: OrderLine[];
    receiptNumber?: number;
    tableReference?: string;
    employeeName?: string;
    receiptDate?: Date;
    payments?: ReceiptReport["data"][number]["payments"];
  }) => {
    if (trialEnded) {
      await showDialog({
        title: t("trial_ended_title"),
        message: t("trial_ended_message"),
        buttons: [
          {
            label: t("ok"),
            value: "ok",
            variant: "default",
          },
        ],
      });
      return false;
    }
    const settings = receiptSettings.find((setting) => {
      if (receiptNumber) {
        return setting.type === ReceiptType.receipt;
      } else {
        return setting.type === ReceiptType.pro_forma;
      }
    });

    if (!ticketItems) {
      ticketItems = items.map((item) => ({
        ...item,
        printRouteId: item.printRouteId,
        parentReference: item.parentReference,
        courseId: item.courseId,
        productId: item.productId,
        turnoverGroupName: item.turnoverGroupName,
        turnoverGroupLedgerNumber: item.turnoverGroupLedgerNumber,
        notifyPrintRouteId: item.notifyPrintRouteId,
        vatRateName: item.vatRateName ?? "unknown",
        employeeId: item.employeeId,
      }));
    }

    if (ticketItems.length === 0) return;

    if (!tableReference) {
      tableReference = currentTable?.reference;
    }

    const totalDiscount = ticketItems
      .filter((item) => item.type === OrderLineTypes.discount)
      .reduce((acc, item) => acc + item.value * item.quantity, 0);

    const totalQuantity = ticketItems
      .filter((orderLine) => orderLine.type === OrderLineTypes.product)
      .reduce((acc, item) => acc + item.quantity, 0);

    const groupedByReference = groupBy(ticketItems, "reference");

    const filteredItems = Object.values(groupedByReference).flatMap((items) => {
      const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
      return totalQuantity > 0 ? items : [];
    });

    const cardholderTickets =
      payments
        ?.map((payment) => payment.cardholderTicket)
        .filter((obj) => obj !== undefined) ?? [];

    const paymentDetails = payments
      ?.filter((payment) => payment.cardNumber)
      .map((payment) => ({
        cardLabel: payment.cardLabel,
        cardNumber: payment.cardNumber,
        paidAt: payment.paidAt,
        amount: payment.amount,
      }));

    const updatedProductsV2 = filteredItems
      .filter(
        (item) =>
          item.type === OrderLineTypes.product ||
          item.type === OrderLineTypes.extra,
      )
      .map((item) => {
        const itemValue = item.value * item.quantity;

        // Calculate discount already assigned to a specific item (if any)
        const itemDiscount = ticketItems
          .filter(
            (i) =>
              i.type === OrderLineTypes.discount &&
              i.parentReference === item.reference,
          )
          .reduce(
            (acc, discountItem) =>
              acc + discountItem.value * discountItem.quantity,
            0,
          );

        // Recalculate global discounts proportionally based on net value
        const addedDiscount = filteredItems
          .filter(
            (i) =>
              i.type === OrderLineTypes.discount &&
              i.parentReference === undefined,
          )
          .reduce((acc, discount) => {
            // Only apply the discount if it was created after the item was created
            if (isAfter(discount.createdAt, item.createdAt)) {
              // Compute the total net value of all eligible items
              const eligibleTotal = filteredItems
                .filter(
                  (product) =>
                    product.type === OrderLineTypes.product &&
                    isBefore(product.createdAt, discount.createdAt),
                )
                .reduce(
                  (total, product) => total + product.value * product.quantity,
                  0,
                );

              // Allocate to the item a proportional share based on its net value
              if (eligibleTotal > 0) {
                return acc + discount.value * (itemValue / eligibleTotal);
              }
              return acc;
            }
            return acc;
          }, 0);

        return {
          ...item,
          totalValue: itemValue,
          discount: itemDiscount + addedDiscount,
        };
      });

    const updatedProductsV1 = filteredItems
      .filter(
        (item) =>
          item.type === OrderLineTypes.product ||
          item.type === OrderLineTypes.extra,
      )
      .map((item) => {
        const itemValue = item.value * item.quantity;

        const itemDiscount = ticketItems
          .filter(
            (i) =>
              i.type === OrderLineTypes.discount &&
              i.parentReference === item.reference,
          )
          .reduce((acc, item) => acc + item.value * item.quantity, 0);

        // Calculate added discounts that apply to this item
        const addedDiscount = filteredItems
          .filter(
            (i) =>
              i.type === OrderLineTypes.discount &&
              i.parentReference === undefined,
          )
          .reduce((acc, discount) => {
            // Check if this discount applies to the item based on creation time
            if (isAfter(discount.createdAt, item.createdAt)) {
              // Find the total quantity of items eligible for this discount
              const eligibleQuantity = filteredItems
                .filter(
                  (product) =>
                    product.type === OrderLineTypes.product &&
                    isBefore(product.createdAt, discount.createdAt),
                )
                .reduce((total, product) => total + product.quantity, 0);

              // Distribute the discount proportionally to this item
              if (eligibleQuantity > 0) {
                return (
                  acc + (discount.value * item.quantity) / eligibleQuantity
                );
              }
            }
            return acc;
          }, 0);

        return {
          ...item,
          totalValue: itemValue,
          discount: itemDiscount + addedDiscount,
        };
      });

    let updatedProducts = updatedProductsV2;

    if (receiptDate && receiptDate < new Date(2025, 1, 23)) {
      updatedProducts = updatedProductsV1;
    }

    const groupedItems = groupBy(
      updatedProducts.filter((p) => !p.parentReference),
      "name",
    );

    const groupedVat = groupBy(
      updatedProducts.filter((item) => item.vatRate !== 0),
      "vatRate",
    );

    const totalProducts = updatedProducts.reduce(
      (acc, item) => acc + item.totalValue,
      0,
    );

    const netTotal = updatedProducts.reduce(
      (acc, item) => acc + item.totalValue - item.discount,
      0,
    );

    const printRoute = printRoutes.find(
      (pr) => pr.id === settings?.printRouteId,
    );

    if (!printRoute) {
      throw new Error("Print route not found");
    }

    for (const printer of printers.filter((p) =>
      p.printRoutes.includes(printRoute.id),
    )) {
      const width = printer.width;

      const encoder = new ReceiptPrinterEncoder({
        language: getPrinterLanguage(printer.language),
        columns: width,
        imageMode: printer.imageMode,
      });

      const result = encoder
        .initialize()
        .codepage("windows1252")
        .align("center");

      await new Promise((resolve) => {
        if (settings?.logo?.url) {
          const img = new Image();
          try {
            img.src = `${settings.logo.url}?v=${Date.now()}`;
            img.crossOrigin = "Anonymous";
            img.onload = function () {
              const desiredHeight = 200;
              const aspectRatio = img.width / img.height;
              const newWidth =
                Math.round((desiredHeight * aspectRatio) / 8) * 8;
              result.image(img, newWidth, desiredHeight, "atkinson").newline();
              resolve(true);
            };
            img.onerror = function () {
              resolve(false);
            };
          } catch (e) {
            console.error(e);
            resolve(false);
          }
        } else {
          resolve(true);
        }
      });

      result.text("");

      if (settings?.header) {
        result.text(settings.header).newline().newline();
      }

      result
        .height(2)
        .width(2)
        .bold(true)
        .line(
          settings?.title ??
            userT(receiptNumber ? "Ticket.receipt" : "Ticket.pro_forma_ticket"),
        )
        .newline()
        .bold(false)
        .width(1)
        .height(1)
        .align("left")
        .line(
          userT("Ticket.time", {
            time: format(
              receiptDate ?? new Date(),
              user.clock === ClockTypes.military
                ? "dd-MM-yyyy HH:mm"
                : "MM/dd/yyyy hh:aa a",
            ),
          }),
        );

      if (employeeName) {
        result.line(
          userT("Ticket.employee", {
            employee_name: employeeName,
          }),
        );
      }

      if (tableReference) {
        result.line(userT("Ticket.table", { table_reference: tableReference }));
      }

      if (receiptNumber) {
        result.line(
          userT("Ticket.receipt_number", { receipt_number: receiptNumber }),
        );
      }

      const firstColumnWidth = 4;

      const secondColumnWidth = Math.round(width * 0.5);

      result.line("-".repeat(width));

      result.width(1);

      Object.entries(groupedItems).map(([name, items]) => {
        const baseName = name.split("-")[0] ?? "";
        let totalQuantity = 0;

        const itemsWithChildrenMap = new Map<
          string,
          { item: OrderLine; children: OrderLine[]; quantity: number }
        >();

        // Process items, focusing on grouping items with the same children
        items.forEach((item) => {
          const children = updatedProducts.filter(
            (i) => i.parentReference === item.reference,
          );

          if (children.length > 0) {
            // Create a unique key to group by children
            const childrenKey = JSON.stringify(
              children.map((child) => child.name).sort(),
            );

            if (!itemsWithChildrenMap.has(childrenKey)) {
              itemsWithChildrenMap.set(childrenKey, {
                item,
                children,
                quantity: 0,
              });
            }

            // Add the quantity to the combined group
            const existingGroup = itemsWithChildrenMap.get(childrenKey);
            if (!existingGroup) return;
            existingGroup.quantity += item.quantity;
          } else {
            // Only count items without children
            totalQuantity += item.quantity;
          }
        });

        itemsWithChildrenMap.forEach(({ item, children, quantity }) => {
          if (quantity <= 0) {
            return;
          }

          result.table(
            [
              {
                width: firstColumnWidth,
                align: "left",
                marginRight: 1,
              },
              {
                width: secondColumnWidth,
                align: "left",
              },
              {
                width: width - firstColumnWidth - secondColumnWidth - 1,
                align: "right",
              },
            ],
            [
              [
                `${quantity}x`,
                baseName,
                formatIntToCurrency(item.value * quantity, true),
              ],
            ],
          );

          const groupedChildren = groupBy(children, "reference");

          const filteredChildren = Object.values(groupedChildren)
            .filter(
              (group) =>
                group.reduce((sum, child) => sum + (child.quantity || 0), 0) >
                0,
            )
            .flat(); // Flatten to get back the array of individual children

          // Print children as indented lines
          filteredChildren.forEach((child) => {
            if (child.type === OrderLineTypes.product) {
              result.table(
                [
                  {
                    width: firstColumnWidth,
                    align: "left",
                    marginRight: 1,
                  },
                  {
                    width: secondColumnWidth,
                    align: "left",
                  },
                  {
                    width: width - firstColumnWidth - secondColumnWidth - 1,
                    align: "right",
                  },
                ],
                [["", `   +${child.name}`, formatIntToCurrency(child.value)]],
              );
            }
          });
        });

        // Render items without children
        if (totalQuantity !== 0) {
          result.table(
            [
              {
                width: firstColumnWidth,
                align: "left",
                marginRight: 1,
              },
              {
                width: secondColumnWidth,
                align: "left",
              },
              {
                width: width - firstColumnWidth - secondColumnWidth - 1,
                align: "right",
              },
            ],
            [
              [
                `${totalQuantity}x`,
                baseName,
                formatIntToCurrency(
                  items
                    .filter(
                      (item) =>
                        !updatedProducts.some(
                          (i) => i.parentReference === item.reference,
                        ),
                    )
                    .reduce((acc, item) => acc + item.value * item.quantity, 0),
                  true,
                ),
              ],
            ],
          );
        }
      });

      result
        .width(2)
        .line("-".repeat(width / 2))
        .width(1);

      result.line(
        printLeftRight(
          userT("Ticket.quantity"),
          totalQuantity.toString(),
          width,
        ),
      );

      result.rule({ style: "single" });

      if (totalDiscount > 0) {
        result.line(
          printLeftRight(
            userT("Ticket.subtotal"),
            formatIntToCurrency(totalProducts, true),
            width,
          ),
        );

        result.line(
          printLeftRight(
            userT("Ticket.discount"),
            `- ${formatIntToCurrency(totalDiscount, true)}`,
            width,
          ),
        );
      }

      result
        .width(2)
        .bold(true)
        .line(
          printLeftRight(
            userT("Ticket.total"),
            formatIntToCurrency(netTotal, true),
            width / 2,
          ),
        )
        .width(1)
        .bold(false);

      result.rule({ style: "single" });

      payments?.forEach((payment) => {
        result.line(
          printLeftRight(
            payment.name,
            formatIntToCurrency(payment.amount),
            width,
          ),
        );
      });

      if (payments) {
        result.rule({ style: "single" });
      }

      const vatFirstColumnWidth = Math.round(width * 0.25);
      const vatSecondColumnWidth = Math.round(width * 0.25);
      const vatthirdColumnWidth = Math.round(width * 0.25);
      const vatFourthColumnWidth =
        width -
        vatFirstColumnWidth -
        vatSecondColumnWidth -
        vatthirdColumnWidth;

      result.table(
        [
          {
            width: vatFirstColumnWidth,
            align: "left",
          },
          {
            width: vatSecondColumnWidth,
            align: "left",
          },
          {
            width: vatthirdColumnWidth,
            align: "right",
          },
          {
            width: vatFourthColumnWidth,
            align: "right",
          },
        ],
        [
          [
            userT("Ticket.vat_rate").toUpperCase(),
            userT("Ticket.net_total").toUpperCase(),
            userT("Ticket.vat_total").toUpperCase(),
            userT("Ticket.gross_total").toUpperCase(),
          ],
        ],
      );

      result
        .width(1)
        .bold(false)
        .table(
          [
            {
              width: vatFirstColumnWidth,
              align: "left",
            },
            {
              width: vatSecondColumnWidth,
              align: "left",
            },
            {
              width: vatthirdColumnWidth,
              align: "right",
            },
            {
              width: vatFourthColumnWidth,
              align: "right",
            },
          ],
          Object.entries(groupedVat).map(([vatRate, groupedItems]) => {
            return [
              `${vatRate}%`,
              formatIntToCurrency(
                groupedItems.reduce(
                  (acc, item) =>
                    acc +
                    ((item.value * item.quantity - item.discount) /
                      (100 + item.vatRate)) *
                      100,
                  0,
                ),
                false,
              ),
              formatIntToCurrency(
                groupedItems.reduce(
                  (acc, item) =>
                    acc +
                    ((item.value * item.quantity - item.discount) /
                      (100 + item.vatRate)) *
                      item.vatRate,
                  0,
                ),
                false,
              ),
              formatIntToCurrency(
                groupedItems.reduce(
                  (acc, item) =>
                    acc + item.value * item.quantity - item.discount,
                  0,
                ),
                false,
              ),
            ];
          }),
        );

      if (paymentDetails && paymentDetails.length > 0) {
        result.newline();
        result.align("center");
        result.bold(true);
        result.size(1, 1);
        result.line("Kopie kaarthouder");
        result.newline();
        result.bold(false);
        result.size(1, 1);
        paymentDetails.forEach((p) => {
          if (user.companyName) {
            result.line(user.companyName);
          }

          if (user.city) {
            result.line(user.city);
          }

          result.newline();

          if (p.cardLabel && p.cardNumber) {
            result.line(
              `${p.cardLabel.toUpperCase()} (***********${p.cardNumber})`,
            );
          }
          result.newline();
          result.line(`BETALING`);

          if (p.paidAt) {
            result.line(format(p.paidAt, "dd-MM-yyyy HH:mm"));
          }
          result.line(`Totaal: ${formatIntToCurrency(p.amount, false)}`);
          result.newline();
          result.line(`AKKOORD`);
        });
      }

      if (cardholderTickets.length > 0) {
        const knownFields = [
          "Automaat:",
          "Verkoper:",
          "Betaling:",
          "Kaart volgnr:",
          "Geldig tot:",
          "Datum:",
          "Auth. code:",
          "Totaal:",
        ];
        result.newline();
        result.align("center");
        result.bold(true);
        result.size(2, 1);
        result.line("Kopie kaarthouder");
        result.newline();
        result.bold(false);
        result.size(1, 1);

        cardholderTickets.forEach((cardholderTicket) => {
          const decoded = atob(cardholderTicket);
          const lines = decoded.split("\n");

          const startIndex = lines.findIndex((line) =>
            line.trim().startsWith("------"),
          );

          const filteredLines =
            startIndex !== -1 ? lines.slice(startIndex + 1) : lines;

          filteredLines.forEach((line) => {
            const trimmedLine = line.trim();

            // Skip completely empty lines for better readability
            if (!trimmedLine) {
              result.text("");
              return;
            }

            // Handle known fields
            const knownField = knownFields.find((field) =>
              trimmedLine.startsWith(field),
            );
            if (knownField) {
              const [key, ...rest] = trimmedLine.split(/:\s*/); // Split into key and value
              const value = rest.join(":"); // Rejoin in case of colon in value (e.g., time)
              if (key && value) {
                result.text(printLeftRight(`${key}:`, value, printer.width));
              } else {
                result.text(formatLine(trimmedLine, printer.width, "left"));
              }
              return;
            }

            // Handle separators
            if (trimmedLine.startsWith("-")) {
              result.text("-".repeat(printer.width));
              return;
            }

            // Center align titles or unknown lines
            if (trimmedLine.length <= printer.width) {
              result.text(formatLine(trimmedLine, printer.width, "left"));
            } else {
              // For long lines, wrap text
              const wrappedLines =
                trimmedLine.match(new RegExp(`.{1,${printer.width}}`, "g")) ??
                [];
              wrappedLines.forEach((wrappedLine) =>
                result.text(formatLine(wrappedLine, printer.width, "left")),
              );
            }
          });
        });
      }

      if (settings?.footer) {
        result.newline().align("center").text(settings.footer).newline();
      }

      result.newline();
      result.newline();
      result.newline();
      result.newline();
      result.newline();
      result.newline();
      result.newline();

      result.cut("partial");

      const encoded = result.encode();

      void sendToPrinter(encoded, printer);
    }
  };

  const initializeCheckout = async () => {
    const { permission } = await hasPermission({
      requiredPermission: Permissions.checkout_order,
    });
    if (!permission) return;
    setCheckoutOpen(true);
  };

  const transferItemsHandler = async ({
    items,
    orderId,
    tableId,
  }: {
    items: CartItem[];
    orderId?: string;
    tableId: string;
  }) => {
    const { employee, permission } = await hasPermission({
      requiredPermission: Permissions.update_order,
    });

    if (!permission) return;

    //TODO: Fix combine and split

    // if (orders.find((order) => order.tableId === tableId)) {
    //   const result = await showDialog({
    //     title: "Combine orders?",
    //     message:
    //       "This table already has an order. Do you want to combine them?",
    //     buttons: [
    //       {
    //         label: "Combine",
    //         value: "combine",
    //         variant: "default",
    //       },
    //       {
    //         label: "Split order",
    //         value: "split",
    //         variant: "default",
    //       },
    //       {
    //         label: "Cancel",
    //         value: "cancel",
    //         variant: "destructive",
    //       },
    //     ],
    //   });
    //   if (result === "cancel") {
    //     reset();

    //     setTransferItems([]);
    //     return;
    //   } else if (result === "split") {
    //     transferItems({
    //       items,
    //       tableId,
    //       orderId: orderId ?? uuidv4(),
    //       combine: true,
    //     });
    //   } else if (result === "combine") {
    //     transferItems({
    //       items,
    //       tableId,
    //       orderId: orderId ?? uuidv4(),
    //       combine: true,
    //     });
    //   }
    // } else {
    //   transferItems({
    //     items,
    //     tableId,
    //     orderId: orderId ?? uuidv4(),
    //     combine: true,
    //   });
    // }

    const prevItemsDiscountTotal =
      orders
        .find((order) => order.tableId === tableId)
        ?.items.filter(
          (orderItem) =>
            orderItem.type === OrderLineTypes.discount &&
            orderItem.parentReference === undefined,
        )
        .reduce((acc, item) => acc + item.value * item.quantity, 0) ?? 0;

    const orderDiscountTotal =
      items
        .filter(
          (orderItem) =>
            orderItem.type === OrderLineTypes.discount &&
            orderItem.parentReference === undefined,
        )
        .reduce((acc, item) => acc + item.value * item.quantity, 0) +
      prevItemsDiscountTotal;

    if (orderDiscountTotal > 0) {
      await showDialog({
        title: t("Transfer.reset_discount_title"),
        message: t("Transfer.reset_discount_message"),
        buttons: [
          {
            label: t("close"),
            value: "cancel",
            variant: "default",
          },
          // {
          //   label: t("Transfer.reset_discount"),
          //   value: "reset",
          //   variant: "destructive",
          // },
        ],
      });
      setTransferItems([]);

      return;
    }

    transferItems({
      items,
      tableId,
      orderId: orderId ?? uuidv4(),
      combine: true,
    });

    const newItems = items.filter((item) => item.ordered === false);

    reset();

    setTransferItems([]);

    if (newItems.length > 0) {
      try {
        await createPreparationTicketHandler({
          reference: currentOrder?.name,
          employee: employee,
          table: currentTable,
          labelIds: currentOrder?.labelIds ?? [],
          items: newItems.map((item) => ({
            ...item,
            printRouteId: item.printRouteId,
            notifyPrintRouteId: item.notifyPrintRouteId,
            productId: item.productId,
            turnoverGroupName: item.turnoverGroupName,
            turnoverGroupLedgerNumber: item.turnoverGroupLedgerNumber,
            vatRateName: item.vatRateName ?? "unknown",
            courseId: item.courseId,
            parentReference: item.parentReference,
            employeeId: item.employeeId,
          })),
        });
      } catch (e) {
        console.error(e);
        toast.error("Could not print ticket");
      }
    }
  };

  const openOpenOrders = () => {
    setOpenOrdersOpen(true);
  };

  const openSplitOrder = () => {
    setSplitDialogOpen(true);
  };

  const splitOrderHandler = async (
    data: z.infer<ReturnType<typeof SplitOrderSchema>>,
  ) => {
    const parsed = SplitOrderSchema().safeParse(data);

    const { permission, employee } = await hasPermission({
      requiredPermission: Permissions.update_order,
    });

    if (!permission) return null;

    return new Promise<boolean>((resolve) => {
      if (!parsed.success) {
        console.error(parsed.error);
        toast.error("Could not split order.");
        resolve(false);
      }

      if (!parsed.data) return null;

      splitOrder(
        {
          ...parsed.data,
          employeeId: employee?.id,
        },
        {
          onSuccess() {
            resolve(true);
          },
          onError() {
            resolve(false);
          },
        },
      );
    });
  };

  const openOrderTransfer = (order?: Order) => {
    const orderItems =
      order?.items.map((item) => ({
        ...item,
        ordered: true,
        printed: true,
        originalQuantity: item.quantity,
        reference: item.reference,
        vatRate: item.vatRate,
        value: item.value,
        courseId: item.courseId,
        parentReference: item.parentReference ?? undefined,
      })) ?? [];

    setTransferItems(orderItems);
    if (orderItems.length === 0) {
      setTransferDialogOpen(true);
    }
  };

  const updateOrderHandler = async ({
    printTicket = true,
    printProforma = false,
    printAll = false,
    closeAfterUpdate = true,
    order,
  }: {
    printTicket?: boolean;
    printProforma?: boolean;
    printAll?: boolean;
    closeAfterUpdate?: boolean;
    order?: Order;
  }) => {
    if (trialEnded) {
      await showDialog({
        title: t("trial_ended_title"),
        message: t("trial_ended_message"),
        buttons: [
          {
            label: t("ok"),
            value: "ok",
            variant: "default",
          },
        ],
      });
      return null;
    }
    const { permission, employee } = await hasPermission({
      requiredPermission: Permissions.update_order,
    });

    if (!permission) return null;

    if (!currentTable && !order) {
      setNoTableSelectedDialogOpen(true);
      return;
    }

    const orderToUpdate = order ?? currentOrder;

    const newItems = getNewItems();

    if (orderToUpdate?.items.length !== 0 || newItems.length !== 0) {
      updateOrder({
        updatedAt: new Date(),
        id: orderToUpdate?.id ?? uuidv4(),
        tableId: currentTable?.id ?? orderToUpdate?.tableId,
        orderLines: newItems,
        employeeId: employee?.id,
        partySize: orderToUpdate?.partySize,
        name: orderToUpdate?.name,
        labelIds: orderToUpdate?.labelIds ?? [],
        payments: orderToUpdate?.payments ?? [],
        printTicket: printProforma,
      });

      if (printTicket) {
        const itemsToPrint = printAll
          ? items
          : items.filter((item) => item.printed === false);
        try {
          createPreparationTicketHandler({
            reference: orderToUpdate?.name,
            partySize: orderToUpdate?.partySize,
            employee: employee,
            table: currentTable,
            labelIds: orderToUpdate?.labelIds ?? [],
            items: itemsToPrint.map((item) => ({
              ...item,
              printRouteId: item.printRouteId,
              notifyPrintRouteId: item.notifyPrintRouteId,
              courseId: item.courseId,
              turnoverGroupName: item.turnoverGroupName,
              turnoverGroupLedgerNumber: item.turnoverGroupLedgerNumber,
              productId: item.productId,
              parentReference: item.parentReference,
              employeeId: item.employeeId,
              vatRateName: item.vatRateName ?? "unknown",
            })),
          }).catch((e) => console.error(e));
        } catch (e) {
          console.error(e);
          toast.error("Could not print ticket");
        }
      }

      if (printProforma) {
        const table = tables.find((t) => t.id === orderToUpdate?.tableId);
        if (orderToUpdate) {
          createAudit({
            orderId: orderToUpdate.id,
            auditType: OrderAuditTypes.pro_forma_print,
            employeeId: employee?.id,
          });
        }
        try {
          createTicket({
            ticketItems: items.map((item) => ({
              ...item,
              printRouteId: item.printRouteId,
              notifyPrintRouteId: item.notifyPrintRouteId,
              courseId: item.courseId,
              productId: item.productId,
              turnoverGroupName: item.turnoverGroupName,
              turnoverGroupLedgerNumber: item.turnoverGroupLedgerNumber,
              parentReference: item.parentReference,
              employeeId: item.employeeId,
              vatRateName: item.vatRateName ?? "unknown",
            })),
            tableReference: table?.reference,
            employeeName: employee?.firstName,
          }).catch((e) => console.error(e));
        } catch (e) {
          console.error(e);
          toast.error("Could not print ticket");
        }
      }
    }

    if (signOutAfterAction) {
      if (platform !== "ANDROID" && platform !== "IOS" && platform !== "PWA") {
        logout();
      }
    }

    reset();

    setUnsavedChanges({
      isDirty: false,
    });

    if (closeAfterUpdate) {
      await navigate({ to: "/floor-plan", ignoreBlocker: true });
    }

    return true;
  };

  const finalizeOrderHandler = async () => {
    if (trialEnded) {
      await showDialog({
        title: t("trial_ended_title"),
        message: t("trial_ended_message"),
        buttons: [
          {
            label: t("ok"),
            value: "ok",
            variant: "default",
          },
        ],
      });
      return null;
    }

    const { permission, employee } = await hasPermission({
      requiredPermission: Permissions.checkout_order,
    });

    return new Promise<boolean>((resolve) => {
      console.log("woell");
      if (!permission) {
        return resolve(false);
      }

      setIsLoading(true);

      const itemsToPrint = items.filter((item) => item.printed === false);

      finalizeOrder(
        {
          updatedAt: new Date(),
          id: currentOrder?.id ?? uuidv4(),
          tableId: currentTable?.id,
          orderLines: getNewItems(),
          employeeId: employee?.id,
          labelIds: currentOrder?.labelIds ?? [],
          payments: currentOrder?.payments ?? [],
        },
        {
          async onSuccess(result, { id: orderId }) {
            createPreparationTicketHandler({
              reference: currentOrder?.name,
              employee: employee,
              table: currentTable,
              labelIds: currentOrder?.labelIds ?? [],
              items: itemsToPrint.map((item) => ({
                ...item,
                printRouteId: item.printRouteId,
                notifyPrintRouteId: item.notifyPrintRouteId,
                turnoverGroupName: item.turnoverGroupName,
                turnoverGroupLedgerNumber: item.turnoverGroupLedgerNumber,
                courseId: item.courseId,
                productId: item.productId,
                parentReference: item.parentReference,
                employeeId: item.employeeId,
                vatRateName: item.vatRateName ?? "unknown",
              })),
            }).catch((e) => {
              console.error(e);
              toast.error("Could not print ticket");
            });

            setIsLoading(false);

            if (signOutAfterAction) {
              if (
                platform !== "ANDROID" &&
                platform !== "IOS" &&
                platform !== "PWA"
              ) {
                logout();
              }
            }

            if (result !== null) {
              setSelectedReceipt({
                receipt: {
                  ...result,
                  isCredit: false,
                  table: result.table.reference,
                },
                showCredit: false,
              });

              setReceiptDialogOpen(true);
            }

            handleOrderDelete(orderId);

            await navigate({ to: "/floor-plan", ignoreBlocker: true });

            reset();

            setUnsavedChanges({
              isDirty: false,
            });

            // setCurrentOrder(undefined);

            return resolve(true);
          },
          onError() {
            setIsLoading(false);
            resolve(false);
          },
        },
      );
    });
  };

  const updatePaymentStatus = ({
    orderId,
    paymentId,
    paymentStatus,
  }: {
    orderId: string;
    paymentId: string;
    paymentStatus: PaymentStatus;
  }) => {
    const prevData = utils.order.getActiveOrders.getData();

    utils.order.getActiveOrders.setData(undefined, (old) => {
      if (!old) return prevData;

      const previousOrder = old.find((order) => order.id === orderId);

      if (!previousOrder) {
        return [...old];
      }

      return [
        ...old.filter((order) => order.id !== previousOrder.id),
        {
          ...previousOrder,
          payments: previousOrder.payments.map((payment) => {
            if (payment.id === paymentId) {
              return {
                ...payment,
                paymentStatus,
              };
            }
            return payment;
          }),
        },
      ];
    });
  };

  const startTransfer = () => {
    void navigate({ to: "/floor-plan", ignoreBlocker: true });
  };

  const cancelTransfer = () => {
    setTransferItems([]);
  };

  useEffect(() => {
    setTransferingItems(transferingItems);
  }, [setTransferingItems, transferingItems]);

  return {
    splitOrder: splitOrderHandler,
    finalizeOrder: finalizeOrderHandler,
    openSplitOrder,
    updatePaymentStatus,
    startTransfer,
    addPayment: addPaymentHandler,
    unFinalizeOrder: unfinalizeOrderHandler,
    voidPayment: voidPaymentHandler,
    createTerminalPayment: createTerminalPaymentHandler,
    cancelTransfer,
    openOrderTransfer,
    openOnlineOrderDialog,
    createPreparationTicket: createPreparationTicketHandler,
    updateOrder: updateOrderHandler,
    deleteOrder: deleteOrderHandler,
    transferItems: transferItemsHandler,
    createTicket,
    openOpenOrders,
    initializeCheckout,
    nextRound: nextRoundHandler,
  };
};

export const useOrderUpdates = () => {
  const utils = api.useUtils();
  const getSelectedDate = useTempBookingStore((state) => state.getSelectedDate);

  const addToCurrentOnlineOrderQueue = useOrderStore(
    (state) => state.addToCurrentOnlineOrderQueue,
  );

  const removeFromCurrentOnlineOrderQueue = useOrderStore(
    (state) => state.removeFromCurrentOnlineOrderQueue,
  );

  const refreshTableOrders = async (tableId: string) => {
    const refreshedOrders = await utils.order.getActiveOrders.fetch(
      {
        tableId,
      },
      { networkMode: "always" },
    );

    void utils.order.getActiveOrders.setData(undefined, (old) => {
      if (!old) return refreshedOrders;

      // Remove all orders with the specified tableId from the old list
      const remainingOrders = old.filter(
        (oldOrder) => oldOrder.tableId !== tableId,
      );

      // Add the refreshed orders for the specified tableId
      const newOrders = [...remainingOrders, ...refreshedOrders];

      return newOrders;
    });
  };

  const handleOrderDelete = useCallback(
    (orderId: string) => {
      console.log("Should delete order", orderId);

      void utils.order.getActiveOrders.setData(undefined, (old) => {
        if (!old) {
          console.log("NO ACTIVE ORDERS");
          return old;
        }
        console.log(old);
        return old.filter((p) => p.id !== orderId);
      });
    },
    [utils.order.getActiveOrders],
  );

  const handleOrderUpdate = useCallback(
    async (orderId: string) => {
      const result = await utils.order.getById.fetch(orderId, {
        staleTime: 0,
      });

      if (!result) {
        handleOrderDelete(orderId);
        return null;
      }

      void utils.order.getActiveOrders.setData(undefined, (old) => {
        if (!old) return old;
        return [...old.filter((p) => p.id !== result.id), result];
      });

      return result;
    },
    [handleOrderDelete, utils.order.getActiveOrders, utils.order.getById],
  );

  const handleOnlineOrderUpdate = useCallback(
    async (orderId: string) => {
      const selectedDate = getSelectedDate();
      const result = await utils.order.getOnlineOrderById.fetch(orderId, {
        staleTime: 0,
      });

      if (!result) {
        // handleOrderDelete(orderId);
        return null;
      }

      if (result.orderStatus === OrderStatus.new) {
        addToCurrentOnlineOrderQueue(result);
      } else {
        removeFromCurrentOnlineOrderQueue(result);
      }

      void utils.order.getOnlineOrders.setData(
        {
          startDate: startOfDay(subDays(selectedDate, 7)),
        },
        (old) => {
          if (!old) return old;
          return [...old.filter((p) => p.id !== result.id), result];
        },
      );

      return result;
    },
    [
      addToCurrentOnlineOrderQueue,
      getSelectedDate,
      utils.order.getOnlineOrderById,
      utils.order.getOnlineOrders,
    ],
  );

  return {
    handleOrderUpdate,
    handleOnlineOrderUpdate,
    handleOrderDelete,
    refreshTableOrders,
  };
};
